import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useLocation } from 'wouter';
import { toast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Plus, RefreshCw, Trash2 } from 'lucide-react';
import SuperAdminLayout from '@/components/admin/super-admin-layout';
import { NODE_TYPE_META, NodeConfigPanel, getTriggerActionLabel } from './email-templates';

// Full-page node-graph builder for a single "advanced automation" (graph
// engine) workflow — reached from the Workflows tab's "New Advanced
// Automation" and "Edit Graph" actions rather than opened as a modal, since
// the canvas needs real room to lay out a non-trivial node chain.

export default function AutomationBuilderPage() {
  const params = useParams();
  const workflowId = params.id ? parseInt(params.id) : NaN;
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [connectingFrom, setConnectingFrom] = useState<{ nodeId: number; branch: string } | null>(null);
  const [dragging, setDragging] = useState<{ id: number; ox: number; oy: number } | null>(null);
  const [localPositions, setLocalPositions] = useState<Record<number, { x: number; y: number }>>({});
  const [showPalette, setShowPalette] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);

  const workflowsQuery = useQuery({
    queryKey: ['/api/admin/email/workflows'],
    queryFn: async () => {
      const res = await fetch('/api/admin/email/workflows');
      if (!res.ok) throw new Error('Failed to fetch workflows');
      return res.json();
    },
  });
  const workflow = workflowsQuery.data?.workflows?.find((w: any) => w.id === workflowId);

  const templatesQuery = useQuery({
    queryKey: ['/api/admin/email/templates'],
    queryFn: async () => {
      const res = await fetch('/api/admin/email/templates');
      if (!res.ok) throw new Error('Failed to fetch templates');
      return res.json();
    },
  });
  const templates = templatesQuery.data?.templates || [];

  const graphKey = [`/api/admin/email/workflows/${workflowId}/graph`];
  const graphQuery = useQuery({
    queryKey: graphKey,
    queryFn: async () => {
      const res = await fetch(`/api/admin/email/workflows/${workflowId}/graph`);
      if (!res.ok) throw new Error('Failed to fetch graph');
      return res.json();
    },
    enabled: !isNaN(workflowId),
  });

  const tagsQuery = useQuery({
    queryKey: ['/api/admin/tags'],
    queryFn: async () => { const r = await fetch('/api/admin/tags'); if (!r.ok) throw new Error('Failed to fetch tags'); return r.json(); },
  });

  const nodes: any[] = graphQuery.data?.nodes || [];
  const edges: any[] = graphQuery.data?.edges || [];
  const allTags: { id: number; name: string; userCount: number }[] = tagsQuery.data?.tags || [];

  const invalidateGraph = () => queryClient.invalidateQueries({ queryKey: graphKey });

  const createNodeMutation = useMutation({
    mutationFn: (body: any) => apiRequest(`/api/admin/email/workflows/${workflowId}/nodes`, { method: 'POST', data: body }),
    onSuccess: () => invalidateGraph(),
    onError: (err: any) => toast({ title: 'Failed to add node', description: err?.message, variant: 'destructive' }),
  });
  const updateNodeMutation = useMutation({
    mutationFn: ({ nodeId, body }: { nodeId: number; body: any }) => apiRequest(`/api/admin/email/workflows/${workflowId}/nodes/${nodeId}`, { method: 'PUT', data: body }),
    onSuccess: () => invalidateGraph(),
  });
  const deleteNodeMutation = useMutation({
    mutationFn: (nodeId: number) => apiRequest(`/api/admin/email/workflows/${workflowId}/nodes/${nodeId}`, { method: 'DELETE' }),
    onSuccess: () => { invalidateGraph(); setSelectedNodeId(null); },
  });
  const createEdgeMutation = useMutation({
    mutationFn: (body: any) => apiRequest(`/api/admin/email/workflows/${workflowId}/edges`, { method: 'POST', data: body }),
    onSuccess: () => invalidateGraph(),
    onError: (err: any) => toast({ title: 'Could not connect', description: err?.message || 'That connection is not allowed.', variant: 'destructive' }),
  });
  const deleteEdgeMutation = useMutation({
    mutationFn: (edgeId: number) => apiRequest(`/api/admin/email/workflows/${workflowId}/edges/${edgeId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateGraph(),
  });
  const runTestMutation = useMutation({
    mutationFn: () => apiRequest(`/api/admin/email/workflows/${workflowId}/graph/run-test`, { method: 'POST' }),
    onSuccess: (res: any) => { invalidateGraph(); toast({ title: 'Run complete', description: res.message }); },
    onError: (err: any) => toast({ title: 'Run failed', description: err?.message, variant: 'destructive' }),
  });

  const getPos = (node: any) => localPositions[node.id] || { x: node.positionX, y: node.positionY };

  const onNodeMouseDown = (e: React.MouseEvent, node: any) => {
    e.stopPropagation();
    const rect = canvasRef.current!.getBoundingClientRect();
    const pos = getPos(node);
    setDragging({ id: node.id, ox: e.clientX - rect.left - pos.x, oy: e.clientY - rect.top - pos.y });
    setSelectedNodeId(node.id);
  };
  const onCanvasMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = Math.max(0, e.clientX - rect.left - dragging.ox);
    const y = Math.max(0, e.clientY - rect.top - dragging.oy);
    setLocalPositions(p => ({ ...p, [dragging.id]: { x, y } }));
  };
  const onCanvasMouseUp = () => {
    if (dragging) {
      const pos = localPositions[dragging.id];
      if (pos) updateNodeMutation.mutate({ nodeId: dragging.id, body: { positionX: Math.round(pos.x), positionY: Math.round(pos.y) } });
    }
    setDragging(null);
  };

  const onOutPortClick = (e: React.MouseEvent, node: any, branch: string) => {
    e.stopPropagation();
    setConnectingFrom(prev => (prev && prev.nodeId === node.id && prev.branch === branch) ? null : { nodeId: node.id, branch });
  };
  const onInPortClick = (e: React.MouseEvent, node: any) => {
    e.stopPropagation();
    if (!connectingFrom) return;
    if (connectingFrom.nodeId === node.id) { setConnectingFrom(null); return; }
    createEdgeMutation.mutate({ fromNodeId: connectingFrom.nodeId, toNodeId: node.id, branch: connectingFrom.branch });
    setConnectingFrom(null);
  };

  const selectedNode = nodes.find(n => n.id === selectedNodeId);
  const CARD_W = 208;

  const renderEdgeLines = () => edges.map((edge: any) => {
    const from = nodes.find((n: any) => n.id === edge.fromNodeId);
    const to = nodes.find((n: any) => n.id === edge.toNodeId);
    if (!from || !to) return null;
    const fromPos = getPos(from), toPos = getPos(to);
    let x1 = fromPos.x + CARD_W / 2;
    const y1 = fromPos.y + 78;
    if (from.type === 'condition') {
      x1 = edge.branch === 'yes' ? fromPos.x + CARD_W * 0.3 : fromPos.x + CARD_W * 0.7;
    }
    const x2 = toPos.x + CARD_W / 2, y2 = toPos.y;
    const midX = (x1 + x2) / 2, midY = (y1 + y2) / 2;
    const color = edge.branch === 'yes' ? '#10b981' : edge.branch === 'no' ? '#ef4444' : '#6366f1';
    return (
      <g key={edge.id}>
        <path d={`M${x1},${y1} C${x1},${midY} ${x2},${midY} ${x2},${y2}`} stroke={color} strokeWidth="2" fill="none" strokeDasharray="5,4" />
        <circle cx={midX} cy={midY} r={7} fill="white" stroke="#e2e8f0" strokeWidth="1.5" className="cursor-pointer hover:fill-red-50" onClick={() => deleteEdgeMutation.mutate(edge.id)} />
        <text x={midX} y={midY + 4} textAnchor="middle" fontSize="9" fill="#ef4444" className="cursor-pointer select-none" onClick={() => deleteEdgeMutation.mutate(edge.id)}>✕</text>
      </g>
    );
  });

  if (isNaN(workflowId)) {
    return (
      <SuperAdminLayout>
        <div className="p-6 text-sm text-slate-500">Invalid automation id.</div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="p-6 flex flex-col" style={{ height: 'calc(100vh - 1px)' }}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setLocation('/admin/email/templates')}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Workflows
            </Button>
            <div>
              <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                {workflow?.name || 'Advanced Automation'}
                {workflow && (
                  <Badge variant={workflow.status === 'active' ? 'default' : 'secondary'} className={workflow.status === 'active' ? 'bg-emerald-500 text-white hover:bg-emerald-600 text-[10px]' : 'text-[10px]'}>
                    {workflow.status === 'active' ? 'Active' : 'Paused'}
                  </Badge>
                )}
              </h1>
              <p className="text-xs text-slate-500 max-w-xl">
                Click a node's bottom (OUT) port, then another node's top (IN) port to connect them. Condition nodes have two OUT ports — green "Y" for Yes, red "N" for No.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Button size="sm" onClick={() => setShowPalette(p => !p)}><Plus className="h-3.5 w-3.5 mr-1" /> Add Node</Button>
              {showPalette && (
                <div className="absolute right-0 top-9 bg-white border shadow-xl rounded-xl z-50 p-1.5 flex flex-col gap-1 min-w-[140px]">
                  {(['condition', 'action', 'exit'] as const).map(t => (
                    <button
                      key={t}
                      onClick={() => {
                        createNodeMutation.mutate({ type: t, config: {}, positionX: 60 + nodes.length * 30, positionY: 60 + nodes.length * 60 });
                        setShowPalette(false);
                      }}
                      className="flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-lg hover:bg-slate-50 text-slate-700 font-semibold text-left"
                    >
                      <span className={`p-1 rounded text-white ${NODE_TYPE_META[t].badge}`}>{NODE_TYPE_META[t].icon}</span>
                      {NODE_TYPE_META[t].label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => runTestMutation.mutate()} disabled={runTestMutation.isPending}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${runTestMutation.isPending ? 'animate-spin' : ''}`} /> Run Now
            </Button>
            {connectingFrom && (
              <span className="text-xs bg-indigo-50 border border-indigo-200 text-indigo-600 px-2.5 py-1 rounded-lg font-semibold animate-pulse">
                Click a node's IN port to connect…
              </span>
            )}
          </div>
        </div>

        <div className="flex gap-3 flex-1 min-h-0">
          <div
            ref={canvasRef}
            className="relative flex-1 rounded-xl border border-slate-200 shadow-inner overflow-auto cursor-default select-none bg-slate-50/50"
            style={{ backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '18px 18px' }}
            onMouseMove={onCanvasMouseMove}
            onMouseUp={onCanvasMouseUp}
            onMouseLeave={onCanvasMouseUp}
            onClick={() => { setShowPalette(false); setConnectingFrom(null); }}
          >
            <svg className="absolute inset-0 pointer-events-none" width="2400" height="1400">
              <g className="pointer-events-auto">{renderEdgeLines()}</g>
            </svg>

            {nodes.map((node: any) => {
              const pos = getPos(node);
              const meta = NODE_TYPE_META[node.type];
              const config = node.config || {};
              return (
                <div
                  key={node.id}
                  className={`absolute rounded-xl border-2 shadow-md bg-white/95 border-slate-200 ${selectedNodeId === node.id ? 'ring-2 ring-indigo-400 ring-offset-1' : ''}`}
                  style={{ left: pos.x, top: pos.y, width: CARD_W }}
                  onMouseDown={(e) => onNodeMouseDown(e, node)}
                >
                  {node.type !== 'trigger' && (
                    <div
                      className={`absolute -top-2.5 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-white text-[9px] font-bold cursor-pointer shadow ${connectingFrom ? 'bg-indigo-500 animate-pulse' : 'bg-slate-400'}`}
                      onClick={(e) => onInPortClick(e, node)}
                      title="IN port"
                    >→</div>
                  )}
                  <div className="flex items-center justify-between px-2 pt-2 pb-1">
                    <div className="flex items-center gap-1.5">
                      <span className={`p-1 rounded text-white ${meta.badge}`}>{meta.icon}</span>
                      <span className="text-[10px] font-bold uppercase text-slate-500">{meta.label}</span>
                    </div>
                    {node.type !== 'trigger' && (
                      <button onMouseDown={e => e.stopPropagation()} onClick={() => deleteNodeMutation.mutate(node.id)} className="text-slate-300 hover:text-red-500">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className="px-2 pb-2 text-[10px] text-slate-600 space-y-1">
                    {node.type === 'trigger' && <div>{config.templateId ? getTriggerActionLabel(config.triggerAction || 'still_not_opened') : "Enrolls everyone — no entry template set"}</div>}
                    {node.type === 'condition' && <div>{(config.tagIds || []).length > 0 ? `Has ${config.matchMode === 'all' ? 'all' : 'any'} of ${(config.tagIds || []).length} tag(s)` : 'No tags set yet'}</div>}
                    {node.type === 'action' && <div className="capitalize">{(config.actionType || 'not set').replace('_', ' ')}{config.actionType === 'wait' ? ` (${config.waitDays || 1}d)` : ''}</div>}
                    {node.type === 'exit' && <div>Ends the automation</div>}
                    <button onMouseDown={e => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); setSelectedNodeId(node.id); }} className="text-indigo-600 font-semibold hover:underline">
                      Configure →
                    </button>
                  </div>

                  {node.type === 'condition' ? (
                    <>
                      <div onClick={(e) => onOutPortClick(e, node, 'yes')} title="OUT: Yes" className={`absolute -bottom-2.5 left-[30%] -translate-x-1/2 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-white text-[8px] font-bold cursor-pointer shadow ${connectingFrom?.nodeId === node.id && connectingFrom?.branch === 'yes' ? 'bg-emerald-600 scale-125 ring-2 ring-emerald-300' : 'bg-emerald-500 hover:scale-110'}`}>Y</div>
                      <div onClick={(e) => onOutPortClick(e, node, 'no')} title="OUT: No" className={`absolute -bottom-2.5 left-[70%] -translate-x-1/2 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-white text-[8px] font-bold cursor-pointer shadow ${connectingFrom?.nodeId === node.id && connectingFrom?.branch === 'no' ? 'bg-red-600 scale-125 ring-2 ring-red-300' : 'bg-red-500 hover:scale-110'}`}>N</div>
                    </>
                  ) : node.type !== 'exit' && (
                    <div onClick={(e) => onOutPortClick(e, node, 'none')} title="OUT port" className={`absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-white text-[9px] font-bold cursor-pointer shadow ${connectingFrom?.nodeId === node.id ? 'bg-indigo-600 scale-125 ring-2 ring-indigo-300' : 'bg-slate-400 hover:bg-indigo-500'}`}>+</div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="w-80 shrink-0 border rounded-xl p-3 overflow-y-auto bg-white">
            {!selectedNode ? (
              <p className="text-xs text-slate-400 text-center py-8">Click "Configure" on a node to edit it here.</p>
            ) : (
              <NodeConfigPanel
                key={selectedNode.id}
                node={selectedNode}
                templates={templates}
                allTags={allTags}
                onSave={(config) => updateNodeMutation.mutate({ nodeId: selectedNode.id, body: { config } })}
                onCreateTag={async (name: string) => {
                  const res: any = await apiRequest('/api/admin/tags', { method: 'POST', data: { name } });
                  queryClient.invalidateQueries({ queryKey: ['/api/admin/tags'] });
                  return res.tag.id;
                }}
              />
            )}
          </div>
        </div>
      </div>
    </SuperAdminLayout>
  );
}
